import { POINT_STRIDE, bboxOfPoints, type BBox, type Stroke } from 'shared';

function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** Toca o traço? Compara com a espessura, então traço grosso tem alvo maior. */
export function strokeHitByPoint(s: Stroke, x: number, y: number, slack: number): boolean {
  const reach = slack + s.size;
  const p = s.points;
  if (p.length < POINT_STRIDE) return false;
  if (p.length === POINT_STRIDE) {
    return Math.hypot(x - (p[0] as number), y - (p[1] as number)) <= reach;
  }
  for (let i = 0; i + POINT_STRIDE * 2 - 1 < p.length; i += POINT_STRIDE) {
    if (distToSegment(x, y, p[i] as number, p[i + 1] as number, p[i + POINT_STRIDE] as number, p[i + POINT_STRIDE + 1] as number) <= reach) {
      return true;
    }
  }
  return false;
}

/** Traço cruzado pelo movimento da borracha, não só pelo ponto final:
 *  em movimento rápido os eventos ficam longe um do outro e sem isso a
 *  borracha "pula" traços. */
export function strokeHitBySegment(s: Stroke, ax: number, ay: number, bx: number, by: number, slack: number): boolean {
  const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / 6));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (strokeHitByPoint(s, ax + (bx - ax) * t, ay + (by - ay) * t, slack)) return true;
  }
  return false;
}

export function pointInPolygon(x: number, y: number, poly: number[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 2; i < poly.length; j = i, i += 2) {
    const xi = poly[i] as number, yi = poly[i + 1] as number;
    const xj = poly[j] as number, yj = poly[j + 1] as number;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** O laço pega o traço quando a maior parte dele está dentro — exigir 100%
 *  faz o usuário ter de circundar com precisão cirúrgica. */
export function strokeInLasso(s: Stroke, poly: number[]): boolean {
  const p = s.points;
  let inside = 0;
  let total = 0;
  for (let i = 0; i < p.length; i += POINT_STRIDE) {
    total++;
    if (pointInPolygon(p[i] as number, p[i + 1] as number, poly)) inside++;
  }
  return total > 0 && inside / total >= 0.6;
}

export function polygonBBox(poly: number[]): BBox {
  return bboxOfPoints(poly, 2);
}

/** Move um traço criando outro: traço é imutável (SPEC §5.1). */
export function translatePoints(points: Float32Array, dx: number, dy: number): Float32Array {
  const out = new Float32Array(points);
  for (let i = 0; i < out.length; i += POINT_STRIDE) {
    out[i] = (out[i] as number) + dx;
    out[i + 1] = (out[i + 1] as number) + dy;
  }
  return out;
}

/**
 * Marca na máscara os pontos que a borracha comeu neste movimento.
 * A máscara pertence ao gesto inteiro e só cresce — é isso que impede o
 * recorte de se realimentar e devorar o traço (ver InkEngine.eraseAt).
 */
export function markErasedPoints(
  points: Float32Array,
  mask: Uint8Array,
  ax: number, ay: number, bx: number, by: number,
  radius: number,
): void {
  for (let i = 0, k = 0; i < points.length; i += POINT_STRIDE, k++) {
    if (mask[k]) continue;
    if (distToSegment(points[i] as number, points[i + 1] as number, ax, ay, bx, by) <= radius) {
      mask[k] = 1;
    }
  }
}

/** Os trechos que sobraram, a partir da máscara. Pedaço com menos de dois
 *  pontos não vira traço: seria um ponto solto no lugar de tinta apagada. */
export function piecesFromMask(points: Float32Array, mask: Uint8Array): Float32Array[] {
  const runs: number[][] = [];
  let current: number[] = [];
  for (let i = 0, k = 0; i < points.length; i += POINT_STRIDE, k++) {
    if (mask[k]) {
      if (current.length >= POINT_STRIDE * 2) runs.push(current);
      current = [];
    } else {
      current.push(
        points[i] as number, points[i + 1] as number,
        points[i + 2] as number, points[i + 3] as number,
      );
    }
  }
  if (current.length >= POINT_STRIDE * 2) runs.push(current);
  return runs.map((r) => new Float32Array(r));
}

/** O traço encosta no retângulo? Usado pela seleção retangular. */
export function strokeInRect(s: Stroke, rect: [number, number, number, number]): boolean {
  const [x0, y0, x1, y1] = rect;
  const p = s.points;
  let inside = 0;
  let total = 0;
  for (let i = 0; i < p.length; i += POINT_STRIDE) {
    total++;
    const x = p[i] as number;
    const y = p[i + 1] as number;
    if (x >= x0 && x <= x1 && y >= y0 && y <= y1) inside++;
  }
  return total > 0 && inside / total >= 0.6;
}
